import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2, Eye, EyeOff, Save, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ShowcaseModerationPanel() {
  const qc = useQueryClient();
  const profiles = useQuery({ queryKey: ["admin-showcase"], queryFn: () => api.get("/admin/showcase/profiles").then((r) => r.data) });
  const catsQ = useQuery({ queryKey: ["admin-showcase-cats"], queryFn: () => api.get("/admin/showcase/categories").then((r) => r.data.categories) });
  const [catText, setCatText] = useState("");
  const [savingCats, setSavingCats] = useState(false);

  useEffect(() => { if (catsQ.data) setCatText(catsQ.data.join(", ")); }, [catsQ.data]);

  const items = profiles.data || [];

  const toggle = async (p) => {
    try {
      await api.put(`/admin/showcase/profiles/${p.id}/visibility`, { visible: p.visible === false });
      toast.success(p.visible === false ? "Profile shown" : "Profile hidden");
      qc.invalidateQueries({ queryKey: ["admin-showcase"] });
      qc.invalidateQueries({ queryKey: ["showcase-profiles"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}'s Showcase profile? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/showcase/profiles/${p.id}`);
      toast.success("Profile deleted");
      qc.invalidateQueries({ queryKey: ["admin-showcase"] });
      qc.invalidateQueries({ queryKey: ["showcase-profiles"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveCats = async () => {
    setSavingCats(true);
    try {
      const list = catText.split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.put("/admin/showcase/categories", { list });
      setCatText((data.categories || []).join(", "));
      toast.success("Categories saved");
      qc.invalidateQueries({ queryKey: ["showcase-cats"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingCats(false); }
  };

  return (
    <div data-testid="admin-showcase-panel" className="space-y-8">
      <div>
        <p className="eyw-label mb-1 flex items-center gap-1.5"><Users size={13} /> Community Showcase</p>
        <h2 className="font-serif text-2xl text-[#1A242B]">Showcase moderation</h2>
        <p className="text-sm text-[#4A5D6B] mt-1">Member profiles go live immediately. Hide or remove anything inappropriate.</p>
      </div>

      <div className="eyw-card p-5">
        <label className="text-sm font-medium text-[#1A242B] mb-1 block">Categories (comma separated)</label>
        <div className="flex flex-col sm:flex-row gap-3">
          <input value={catText} onChange={(e) => setCatText(e.target.value)} data-testid="admin-showcase-categories" className="eyw-input flex-1" placeholder="Reiki, Sound Healing, Yoga…" />
          <button onClick={saveCats} disabled={savingCats} data-testid="admin-showcase-categories-save" className="eyw-btn !py-2.5 text-sm disabled:opacity-60">
            {savingCats ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Save
          </button>
        </div>
      </div>

      {profiles.isLoading ? (
        <div className="py-10 grid place-items-center text-[#8FA89B]"><Loader2 className="animate-spin" size={22} /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-[#4A5D6B]" data-testid="admin-showcase-empty">No Showcase profiles yet.</p>
      ) : (
        <div className="eyw-card divide-y divide-[#E5E0D8]">
          {items.map((p) => (
            <div key={p.id} data-testid={`admin-showcase-row-${p.id}`} className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {p.photo ? <img src={p.photo} alt={p.name} className="h-11 w-11 rounded-xl object-cover" /> : <span className="h-11 w-11 rounded-xl bg-[#5C8A72]/15 text-[#5C8A72] grid place-items-center font-serif text-lg">{(p.name || "?").slice(0, 1)}</span>}
                <div className="min-w-0">
                  <p className="font-medium text-[#1A242B] truncate flex items-center gap-2">{p.name}
                    {p.visible === false && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-100 text-red-600">Hidden</span>}
                  </p>
                  <p className="text-xs text-[#8FA89B] truncate">{p.category || "No category"} · {p.contact_email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggle(p)} data-testid={`admin-showcase-toggle-${p.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs">
                  {p.visible === false ? <><Eye size={13} /> Show</> : <><EyeOff size={13} /> Hide</>}
                </button>
                <button onClick={() => remove(p)} data-testid={`admin-showcase-delete-${p.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs !border-red-300 !text-red-600 hover:!bg-red-500"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
